import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PAYMENT_TYPES = [
  'Cash', 'Check', 'Store Credit',
  'Mastercard', 'Visa', 'Discover', 'Amex',
  'Synchrony', 'Coupon', 'Gift Card'
];

export default function PaymentModal({ isOpen, onClose, transactionId, transactionTotal, onPaymentSuccess, editingPayment, customerId }) {
  const [paymentType, setPaymentType] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [storeCreditBalance, setStoreCreditBalance] = useState(null);

  // Fetch store credit balance when "Store Credit" is selected
  React.useEffect(() => {
    let cancelled = false;
    const fetchBalance = async () => {
      if (paymentType === 'Store Credit' && customerId) {
        try {
          const res = await fetch(`${API_URL}/store-credits/balance/${customerId}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) {
              setStoreCreditBalance(data.total_balance);
            }
          }
        } catch (err) {
          console.error('Failed to fetch store credit balance:', err);
        }
      } else {
        if (!cancelled) {
          setStoreCreditBalance(null);
        }
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [paymentType, customerId]);

  // Reset or populate form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (editingPayment) {
        setPaymentType(editingPayment.payment_type);
        setAmount(parseFloat(editingPayment.amount).toFixed(2));
        setReferenceNumber(editingPayment.reference_number || '');
        setNotes(editingPayment.notes || '');
        setPaymentDate(editingPayment.payment_date ? editingPayment.payment_date.split('T')[0] : new Date().toISOString().split('T')[0]);
      } else {
        setPaymentType('');
        setAmount('');
        setReferenceNumber('');
        setNotes('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
      }
      setError(null);
      setStoreCreditBalance(null);
    }
  }, [isOpen, editingPayment]);

  if (!isOpen) return null;

  const isRefundMode = transactionTotal < 0;
  const remainingBalance = transactionTotal - (editingPayment ? parseFloat(amount) : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // For refunds, send negative amount
      const finalAmount = isRefundMode ? -Math.abs(parseFloat(amount)) : parseFloat(amount);
      const payload = {
        amount: finalAmount,
        payment_type: paymentType,
        reference_number: referenceNumber || null,
        notes: notes || null,
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : null
      };

      let url, method;
      if (editingPayment) {
        url = `${API_URL}/transactions/payments/${editingPayment.id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/transactions/${transactionId}/payments`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to save payment';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Response was not valid JSON, use generic message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (onPaymentSuccess) onPaymentSuccess(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  // Show reference number field for Check and card types
  const showReferenceField = ['Check', 'Mastercard', 'Visa', 'Discover', 'Amex', 'Synchrony', 'Gift Card'].includes(paymentType);

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {isRefundMode ? 'Issue Refund' : (editingPayment ? 'Edit Payment' : 'Add Payment')}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4" autoComplete="off">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
                {error}
              </div>
            )}

            {/* Payment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Type *
              </label>
              <select
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">Select payment type...</option>
                {PAYMENT_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isRefundMode ? 'Refund Amount *' : 'Amount *'}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  autoComplete="off"
                  step="0.01"
                  min="0.01"
                  max={isRefundMode ? Math.abs(transactionTotal) : (paymentType === 'Store Credit' && storeCreditBalance !== null ? Math.min(storeCreditBalance, transactionTotal) : transactionTotal)}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  placeholder="0.00"
                  className={`w-full pl-7 pr-3 py-2 border rounded-md focus:ring-2 focus:border-indigo-500 text-sm ${
                    isRefundMode ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'
                  }`}
                />
              </div>
              {isRefundMode && (
                <>
                  <div className="text-xs mt-1 font-medium text-red-600">
                    Refund Due: ${Math.abs(transactionTotal).toFixed(2)}
                  </div>
                  {!editingPayment && (
                    <button
                      type="button"
                      onClick={() => setAmount(Math.abs(transactionTotal).toFixed(2))}
                      className="text-xs text-red-600 hover:text-red-700 mt-1 font-medium"
                    >
                      Fill with full refund: ${Math.abs(transactionTotal).toFixed(2)}
                    </button>
                  )}
                </>
              )}
              {!isRefundMode && paymentType === 'Store Credit' && storeCreditBalance !== null && (
                <div className={`text-xs mt-1 font-medium ${storeCreditBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Available Store Credit: ${storeCreditBalance.toFixed(2)}
                </div>
              )}
              {!editingPayment && !isRefundMode && transactionTotal > 0 && paymentType !== 'Store Credit' && (
                <button
                  type="button"
                  onClick={() => setAmount(transactionTotal.toFixed(2))}
                  className="text-xs text-indigo-600 hover:text-indigo-700 mt-1 font-medium"
                >
                  Fill with remaining balance: ${transactionTotal.toFixed(2)}
                </button>
              )}
              {!editingPayment && !isRefundMode && paymentType === 'Store Credit' && storeCreditBalance !== null && storeCreditBalance > 0 && (
                <button
                  type="button"
                  onClick={() => setAmount(Math.min(storeCreditBalance, transactionTotal).toFixed(2))}
                  className="text-xs text-indigo-600 hover:text-indigo-700 mt-1 font-medium"
                >
                  Use maximum available: ${Math.min(storeCreditBalance, transactionTotal).toFixed(2)}
                </button>
              )}
            </div>

            {/* Reference Number */}
            {showReferenceField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {paymentType === 'Check' ? 'Check Number' : 'Reference Number'}
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder={paymentType === 'Check' ? 'e.g. 1234' : 'e.g. last 4 digits'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            )}

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date
              </label>
              <input
                type="date"
                autoComplete="off"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                autoComplete="off"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                  isRefundMode ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {saving ? 'Saving...' : (isRefundMode ? 'Issue Refund' : (editingPayment ? 'Update Payment' : 'Add Payment'))}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
